import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { api } from '../lib/api';
import { ART } from '../lib/art';
import { FALLBACK_CITIES } from '../lib/cities';
import { FAKE_REVIEWS } from '../lib/reviews';
import { DEFAULT_CITY_IMAGE, citySlug } from './Cities';

const VIBES = [
  { e: '👻', label: 'Haunted spots', tag: 'haunted-spots' },
  { e: '🔮', label: 'Witchy corners', tag: 'witchy-corners' },
  { e: '📜', label: 'Dark history', tag: 'dark-history' },
  { e: '☕', label: 'Weird cafés', tag: 'weird-cafes' },
  { e: '🌙', label: 'Local legends', tag: 'local-legends' },
  { e: '🃏', label: 'Tarot & occult', tag: 'tarot-occult' },
  { e: '🚶', label: 'Mystery walks', tag: 'mystery-walks' },
  { e: '✨', label: 'Photo spots', tag: 'photo-spots' },
];

export default function Home() {
  const [cities, setCities] = useState(FALLBACK_CITIES);
  useEffect(() => { api.get('/cities').then(r => setCities(r.data?.length ? r.data : FALLBACK_CITIES)).catch(()=>{}); }, []);

  return (
    <div>
      {/* Hero */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-14 pb-16 grid lg:grid-cols-[1.2fr_1fr] gap-10 items-center">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-heading font-semibold leading-[1] tracking-tight mb-5">
            Visit the <span className="bg-gradient-to-r from-[#f97316] via-[#f97316] to-[#f97316] bg-clip-text text-transparent">mysterious side</span> of any city.
          </h1>
          <p className="text-lg text-[#a3a3a3] mb-8 max-w-xl leading-relaxed">
            Tell us your vibe and we’ll match you with a local Ghostie who knows the folklore, the odd little corners, the candlelit cafés and the stories that make a city feel deliciously haunted.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to="/book" className="btn-primary text-lg" data-testid="hero-book-cta">
              Book it! <ArrowRight size={18}/>
            </Link>
            <Link to="/start" className="btn-secondary text-lg" data-testid="hero-become-cta">
              Become a Ghostie
            </Link>
          </div>
          <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-[#a3a3a3]">
            <span>Customised itineraries</span>
            <span>·</span>
            <span>Global city map</span>
            <span>·</span>
            <span>Solo travelers welcome</span>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7 }}
          className="relative flex justify-center">
          <motion.img src={ART.hero} alt="Ghostie mascot" className="w-full max-w-sm"
            onError={(event) => { event.currentTarget.src = DEFAULT_CITY_IMAGE; }}
            animate={{ y: [0, -12, 0] }} transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}/>
        </motion.div>
      </section>

      {/* What it is — one paragraph instead of a full page */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 py-12 text-center">
        <div className="badge-cozy mb-4">What you're booking</div>
        <p className="text-2xl sm:text-3xl font-heading leading-tight">
          A 2–6 hour private hangout with a local enthusiast of folklore, mystery and dark travel. They pick up where guidebooks stop.
        </p>
      </section>

      {/* How it works — 3 steps, simple */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-12 grid sm:grid-cols-3 gap-4">
          {[
          { e: '🗺️', t: '1. Pick a city & date' },
          { e: '👻', t: '2. We match your vibe' },
          { e: '✨', t: '3. Enjoy the weird side of the city!' },
        ].map((s, i) => (
          <motion.div key={i} className="magical-card p-6"
            initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} transition={{ delay: i * 0.08 }}>
            <div className="text-4xl mb-3">{s.e}</div>
            <div className="font-heading text-lg">{s.t}</div>
          </motion.div>
        ))}
      </section>

      {/* Cities */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
        <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
          <h2 className="text-3xl sm:text-4xl font-heading font-semibold">Available in</h2>
          <Link to="/cities" className="link-soft text-sm font-bold" data-testid="cities-view-all">All cities →</Link>
        </div>
        <div className="flex flex-wrap gap-2">
          {cities.map((c) => (
            <Link to={`/cities/${citySlug(c)}`} key={c.city_id} className={`chip ${c.has_active_hosts ? '' : 'opacity-70'}`} data-testid={`city-${c.city_id}`}>
              {c.name}
            </Link>
          ))}
        </div>
      </section>

      {/* Vibes */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
        <h2 className="text-3xl sm:text-4xl font-heading font-semibold mb-6">Pick your vibe</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {VIBES.map((v, i) => (
            <motion.a key={v.label} href={`/blog?tag=${v.tag}`} className="magical-card p-5 text-center block"
              initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }} transition={{ delay: i * 0.04 }}
              whileHover={{ y: -4, rotate: i % 2 ? 1 : -1 }}>
              <div className="text-4xl mb-2">{v.e}</div>
              <div className="font-heading text-sm">{v.label}</div>
            </motion.a>
          ))}
        </div>
      </section>

      {/* Testimonials */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
        <h2 className="text-3xl sm:text-4xl font-heading font-semibold mb-6">What travelers say</h2>
        <div className="grid md:grid-cols-3 gap-4">
          {FAKE_REVIEWS.slice(0, 3).map((r, i) => (
            <motion.div key={i} className="magical-card p-6"
              initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ delay: i * 0.08 }}>
              <div className="text-[#f97316] mb-3">★★★★★</div>
              <p className="text-base mb-3 leading-relaxed">"{r.text}"</p>
              <div className="text-xs text-[#a3a3a3] font-accent uppercase tracking-widest">{r.first_name}</div>
            </motion.div>
          ))}
        </div>
        <div className="text-center mt-6"><Link to="/reviews" className="link-soft text-sm font-bold" data-testid="all-reviews-link">Read all reviews →</Link></div>
      </section>

      {/* CTA */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-12 mb-10">
        <div className="magical-card p-10 sm:p-14 text-center bg-gradient-to-br from-[#262626] to-[#171717]">
          <motion.img src={ART.party} className="w-32 mx-auto mb-4"
            onError={(event) => { event.currentTarget.src = DEFAULT_CITY_IMAGE; }}
            animate={{ y: [0, -8, 0], rotate: [-3, 3, -3] }} transition={{ duration: 4, repeat: Infinity }}/>
          <h2 className="text-3xl sm:text-5xl font-heading font-semibold mb-3 leading-tight">
            Ready to know<br/>
            <span className="text-[#f97316]">the quirky side</span><br/>
            of your favourite city?
          </h2>
          <p className="text-[#a3a3a3] mb-6">Bring your curiosity and let a local Ghostie open the tiny strange doors most people walk past.</p>
          <Link to="/book" className="btn-primary text-lg" data-testid="footer-cta-book">Book a Ghostie! <ArrowRight size={18}/></Link>
        </div>
      </section>
    </div>
  );
}
